import React from 'react';
import { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { Box, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { CheckCircle, ErrorOutline, Warning as WarningIcon } from '@material-ui/icons';
import { useTranslation } from '@michelin/central-provider';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    maxHeight: '35vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableContainer: {
    flex: 1,
    maxHeight: '100%',
    overflowY: 'auto',
    backgroundColor: 'rgb(249, 229, 234)',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    color: theme.palette.text.primary,
  },
  th: {
    textAlign: 'center',
    borderBottom: `2px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  td: {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    maxWidth: 200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textAlign: 'center',
    textOverflow: 'ellipsis',
  },
  noErrors: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  warningBox: {
    background: '#fff3cd',
    color: '#856404',
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  successBox: {
    background: theme.palette.success.main,
    color: '#fff',
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  linkText: {
    cursor: 'pointer',
    textDecoration: 'underline',
    color: theme.palette.primary.main,
  },
}));

interface ValidationError {
  rowNumber: number;
  field: string;
  issue: string;
  valid?: string[];
}

interface ValidationErrorsTableProps {
  data: {
    validation?: { issues: ValidationError[] };
    validRows?: number;
    invalidRows?: number;
  };
}

export default function ValidationErrorsTable({ data }: ValidationErrorsTableProps) {
  const classes = useStyles();
  const [showTable, setShowTable] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  const issues: ValidationError[] = data?.validation?.issues || [];

  React.useEffect(() => {
    setShowTable(false);
  }, [data]);

  const grouped = useMemo(() => {
    return issues.reduce((acc, issue) => {
      if (!acc[issue.rowNumber]) acc[issue.rowNumber] = [];
      acc[issue.rowNumber].push(issue);
      return acc;
    }, {} as Record<number, ValidationError[]>);
  }, [issues]);

  const rowNumbers = Object.keys(grouped)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div className={classes.container}>
      <Box mb={2}>
        {(data.validRows ?? 0) > 0 && (
          <Box className={classes.successBox}>
            <CheckCircle style={{ marginRight: 8, color: '#fff' }} />
            <Typography variant="body2">
              {data.validRows} {t('row(s) are valid and will be created if you confirm.')}
            </Typography>
          </Box>
        )}
        {(data.invalidRows ?? 0) > 0 && (
          <Box className={classes.warningBox}>
            <WarningIcon style={{ marginRight: 8, color: '#ff9800' }} />
            <Typography variant="body2">
              {data.invalidRows} {t('row(s) contain errors and will not be created.')}
              <br />
              {t('To proceed, update the file with valid values and upload it again.')}
              <span
                className={classes.linkText}
                onClick={() => setShowTable(true)}
                style={{ marginLeft: 4 }}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setShowTable(true);
                }}
              >
                ({t('See errors')})
              </span>
            </Typography>
          </Box>
        )}
      </Box>
      {showTable && (
        <>
          <Typography
            variant="body1"
            style={{
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              fontWeight: 500,
            }}
          >
            <ErrorOutline style={{ marginRight: 8, color: theme.palette.error.main }} />
            {t('Please correct the following errors in your file:')}
          </Typography>

          <div className={classes.tableContainer}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>{t('Row')}</th>
                  <th className={classes.th}>{t('Field')}</th>
                  <th className={classes.th}>{t('Issue')}</th>
                  <th className={classes.th}>{t('Valid Values')}</th>
                </tr>
              </thead>

              <tbody>
                {rowNumbers.map((row) =>
                  grouped[row].map((issue, index) => (
                    <tr key={`${row}-${index}`}>
                      <td className={classes.td}>
                        <strong>{row}</strong>
                      </td>
                      <td className={classes.td} title={issue.field || '—'}>
                        {issue.field}
                      </td>
                      <td className={classes.td} title={issue.issue || '—'}>
                        {issue.issue}
                      </td>
                      <td className={classes.td} title={issue.valid?.join(', ') || '—'}>
                        {issue.valid?.length
                          ? issue.valid.slice(0, 3).join(', ') + (issue.valid.length > 3 ? '…' : '')
                          : '—'}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
